import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "../../dark-hero/colors";
import { PageContainer } from "../../dark-hero/PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";
const GOLD_GRADIENT = "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)";

const heroSectionSx = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: HERO_BG,
  px: { xs: 2, md: 4 },
  minHeight: "100svh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  pb: { xs: "80px", md: "96px" },
};

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(8px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.12 },
};

const REVEAL_TRANSITION = { duration: 0.85, ease: [0.22, 1, 0.36, 1] };

const HERO_ENTER = {
  initial: { y: 26, opacity: 0, filter: "blur(10px)" },
  animate: { y: 0, opacity: 1, filter: "blur(0px)" },
};

const sectionSx = {
  py: { xs: "72px", md: "120px" },
  px: { xs: 2, md: 4 },
  borderTop: `1px solid ${colors.border.subtle}`,
  backgroundColor: HERO_BG,
};

const cardSx = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.05)",
  borderRadius: "18px",
  transition: "border-color 0.3s",
  "&:hover": { borderColor: "rgba(232,163,61,0.2)" },
};

const titleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 2,
};

const descSx = {
  maxWidth: "34rem",
  fontSize: { xs: "0.875rem", md: "0.9375rem" },
  lineHeight: 1.85,
  color: colors.text.secondary,
};

const btnPrimarySx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: HERO_BG,
  background: colors.primary.main,
  px: "32px",
  py: "14px",
  textDecoration: "none",
  clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
  display: "inline-block",
  transition: "transform .25s ease, box-shadow .25s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 10px 30px rgba(232,163,61,0.2)",
  },
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "28px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  display: "inline-block",
  transition: "color .2s, border-color .2s, transform .25s ease",
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.35)",
    transform: "translateY(-2px)",
  },
};

const cardTopAccentSx = {
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: "35%",
  height: "1px",
  pointerEvents: "none",
  background: "linear-gradient(90deg, transparent, rgba(246,185,76,0.85), transparent)",
  boxShadow: "0 0 18px rgba(246,185,76,0.4)",
};

const WHY_CARDS = [
  {
    num: "01 —",
    title: "No real accountability",
    body: "When someone abandons a match or griefs their team, nothing happens. The account resets. The behaviour repeats. The platform shrugs.",
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  },
  {
    num: "02 —",
    title: "Identity is throwaway",
    body: "New accounts are cheap. Reputation doesn't carry. Players who invest in the game compete alongside people who have nothing to lose.",
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </>
    ),
  },
  {
    num: "03 —",
    title: "No queue control",
    body: "You have almost no say in who ends up in your lobby. Map, server, playstyle — the defaults decide for you, and you learn to live with it.",
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
];

const PROMISE_ITEMS = [
  { strong: "Cleaner matches.", body: "Fewer throwaway-account dynamics, fewer players with nothing to lose." },
  { strong: "Stronger trust.", body: "Human verification on every account. Not a formality — a real filter." },
  { strong: "Real queue control.", body: "Memberships that unlock actual preferences — map, server, playstyle — not cosmetic fluff." },
  { strong: "Permanent reputation.", body: "A record that follows you. Across matches, sessions, and time." },
  { strong: "Influence over what gets built.", body: "Early players don't just use the platform. They help shape it." },
];

function Eyebrow({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: 2.5 }}>
      <Box sx={{ width: 24, height: "1px", background: colors.primary.main }} />
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: "0.14em",
          color: colors.primary.main,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionIntro({ label, title, description, sx }) {
  return (
    <MotionBox {...REVEAL} transition={REVEAL_TRANSITION} sx={sx}>
      <Eyebrow>{label}</Eyebrow>
      <Typography component="h2" sx={titleSx}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ ...descSx, mb: { xs: 5, md: 8 } }}>{description}</Typography>
      ) : null}
    </MotionBox>
  );
}

function WhyIcon({ children }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      sx={{ width: 36, height: 36, color: colors.primary.main, mb: 2.5 }}
    >
      {children}
    </Box>
  );
}

export default function AboutPage() {
  return (
    <Box sx={{ backgroundColor: HERO_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <Box component="header" sx={heroSectionSx}>
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            zIndex: 0,
            "@keyframes auroradrift1": {
              "0%, 100%": { transform: "translate(0, 0) scale(1)" },
              "33%": { transform: "translate(5%, 10%) scale(1.08)" },
              "66%": { transform: "translate(-4%, 5%) scale(0.94)" },
            },
            "@keyframes auroradrift2": {
              "0%, 100%": { transform: "translate(0, 0) scale(1)" },
              "40%": { transform: "translate(-7%, -6%) scale(1.07)" },
              "70%": { transform: "translate(4%, -8%) scale(0.97)" },
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: "55%",
              height: 280,
              top: -80,
              left: "-5%",
              filter: "blur(56px)",
              borderRadius: "50%",
              animation: "auroradrift1 22s ease-in-out infinite",
              background:
                "radial-gradient(ellipse at 40% 50%, rgba(232,163,61,0.14), transparent 65%), " +
                "radial-gradient(ellipse at 70% 35%, rgba(184,116,31,0.09), transparent 60%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "45%",
              height: 220,
              top: -60,
              right: "-5%",
              filter: "blur(56px)",
              borderRadius: "50%",
              animation: "auroradrift2 28s ease-in-out infinite",
              background: "radial-gradient(ellipse at 50% 50%, rgba(246,197,111,0.09), transparent 60%)",
            }}
          />
        </Box>
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent 70%)",
          }}
        />

        <PageContainer sx={{ position: "relative", zIndex: 1, maxWidth: "75rem" }}>
          <MotionBox {...HERO_ENTER} transition={REVEAL_TRANSITION}>
            <Eyebrow>About Ouro</Eyebrow>
          </MotionBox>
          <MotionBox
            component="h1"
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.1 }}
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2.25rem", sm: "2.5rem", md: "3.25rem", lg: "3.5rem", xl: "3.75rem" },
              fontWeight: 700,
              letterSpacing: "-0.075em",
              textTransform: "uppercase",
              lineHeight: 1,
              color: colors.text.primary,
              maxWidth: 900,
              mb: 4,
            }}
          >
            Built by{" "}
            <Box component="span" sx={{ background: GOLD_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              players
            </Box>
            ,
            <br />
            now looking for
            <br />
            the first players
            <br />
            who believe.
          </MotionBox>
          <MotionBox {...HERO_ENTER} transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}>
            <Typography
              sx={{
                fontSize: { xs: "0.875rem", md: "1rem" },
                fontWeight: 500,
                lineHeight: 1.65,
                color: "rgba(255,255,255,0.72)",
                maxWidth: "32rem",
                mb: 6,
              }}
            >
              Too many competitive matches feel disposable — because the systems around them make it easy
              for trust, accountability, and player quality to break down. Ouro exists to fix that.
            </Typography>
          </MotionBox>
          <MotionBox
            {...HERO_ENTER}
            transition={{ ...REVEAL_TRANSITION, delay: 0.3 }}
            sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
          >
            <Box component={Link} to={{ pathname: "/", hash: "get-started" }} sx={btnPrimarySx}>
              Join the founding community
            </Box>
            <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
              How it works →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <SectionIntro
            label="Why Ouro exists"
            title={
              <>
                The problem
                <br />
                is the system.
              </>
            }
            description="The issue isn't bad players. It's that existing platforms give them no reason to care — and no real cost when they don't."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: "2px",
            }}
          >
            {WHY_CARDS.map((card, index) => (
              <MotionBox
                key={card.num}
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: index * 0.08 }}
                sx={{
                  ...cardSx,
                  position: "relative",
                  overflow: "hidden",
                  p: { xs: "28px 24px", md: "44px 36px" },
                }}
              >
                <Box aria-hidden sx={cardTopAccentSx} />
                <WhyIcon>{card.icon}</WhyIcon>
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: colors.primary.main,
                    mb: 2,
                  }}
                >
                  {card.num}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: colors.text.primary,
                    mb: 1.25,
                  }}
                >
                  {card.title}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: colors.text.secondary, lineHeight: 1.75 }}>
                  {card.body}
                </Typography>
              </MotionBox>
            ))}
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 5, md: 10 },
              alignItems: "start",
            }}
          >
            <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
              <Eyebrow>The platform promise</Eyebrow>
              <Typography component="h2" sx={titleSx}>
                What Ouro
                <br />
                is built to do.
              </Typography>
              <Typography sx={descSx}>
                Not a replacement for matchmaking. A better version of it — with the trust layer it should
                have had from the start.
              </Typography>
            </MotionBox>
            <MotionBox {...REVEAL} transition={{ ...REVEAL_TRANSITION, delay: 0.08 }}>
              {PROMISE_ITEMS.map((item, index) => (
                <Box
                  key={item.strong}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr",
                    gap: 2.5,
                    py: 2.5,
                    borderBottom: `1px solid ${colors.border.subtle}`,
                    ...(index === 0 ? { borderTop: `1px solid ${colors.border.subtle}` } : {}),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.15em",
                      color: colors.text.muted,
                      pt: "2px",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                  <Typography sx={{ fontSize: "15px", color: colors.text.secondary, lineHeight: 1.7 }}>
                    <Box component="strong" sx={{ color: colors.text.primary, fontWeight: 600 }}>
                      {item.strong}
                    </Box>{" "}
                    {item.body}
                  </Typography>
                </Box>
              ))}
            </MotionBox>
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" },
              gap: { xs: 5, md: 10 },
              alignItems: "center",
            }}
          >
            <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                  mb: 0.75,
                }}
              >
                The team
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2.75rem" },
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                  textTransform: "uppercase",
                  mb: 3.5,
                }}
              >
                About
                <br />
                the team
              </Typography>
              <Typography sx={{ fontSize: "15px", color: colors.text.secondary, lineHeight: 1.85 }}>
                Ouro is built by a small team of engineers and competitive players. We have years of
                experience building software and playing CS.
              </Typography>
            </MotionBox>

            <MotionBox
              {...REVEAL}
              transition={{ ...REVEAL_TRANSITION, delay: 0.08 }}
              sx={{ ...cardSx, position: "relative", overflow: "hidden", p: { xs: "32px", md: "48px" } }}
            >
              <Box aria-hidden sx={cardTopAccentSx} />
              <Typography
                aria-hidden
                sx={{
                  fontSize: "96px",
                  fontWeight: 700,
                  lineHeight: 0.7,
                  color: "rgba(232,163,61,0.12)",
                  fontFamily: "Georgia, serif",
                  position: "absolute",
                  top: 24,
                  left: 32,
                }}
              >
                &ldquo;
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "15px", md: "18px" },
                  fontWeight: 300,
                  color: colors.text.primary,
                  lineHeight: 1.75,
                  mt: 4,
                  mb: 2.5,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Early players are not just signups here. They are the people helping decide whether this
                better version of matchmaking can exist at real scale.
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: colors.text.muted,
                }}
              >
                <Box component="span" sx={{ color: colors.primary.main }}>
                  The Ouro team
                </Box>
              </Typography>
            </MotionBox>
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer sx={{ maxWidth: "75rem" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 5, md: 8 },
              alignItems: "center",
            }}
          >
            <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
              <Eyebrow>A domain with a story</Eyebrow>
              <Typography component="h2" sx={titleSx}>
                Why
                <br />
                ouro
                <Box component="span" sx={{ color: colors.primary.main }}>
                  .is
                </Box>
              </Typography>
              <Typography sx={{ ...descSx, mt: 2.5, mb: 2.5 }}>
                In August 2024, when Ouro was still just an idea and a lot of unanswered questions,{" "}
                <Box component="strong" sx={{ color: colors.text.primary, fontWeight: 600 }}>
                  Björn Heiðar Þórarinsson
                </Box>{" "}
                — known on Discord as bjoerntastic — took the time to walk through how CS2 custom servers
                actually work on Linux.
              </Typography>
              <Typography sx={{ ...descSx, mb: 0 }}>
                A Counter-Strike player since 2000, Björn brought over two decades of experience to that
                conversation and turned a vague ambition into a concrete plan. Without that talk, there is no
                Ouro.
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  p: "18px 22px",
                  background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
                  border: "1px solid rgba(232,163,61,0.2)",
                  borderLeft: `2px solid ${colors.primary.main}`,
                  borderRadius: "4px",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: colors.text.secondary, lineHeight: 1.7 }}>
                  <Box component="strong" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                    The .is top-level domain is Iceland&apos;s.
                  </Box>{" "}
                  Björn is from Iceland. Choosing ouro.is was a small, deliberate nod to the person who helped
                  make this real before it had a single line of code running on a server.
                </Typography>
              </Box>
            </MotionBox>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <MotionBox
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: 0.08 }}
                sx={{ ...cardSx, p: { xs: "32px 28px", md: "56px 48px" }, textAlign: "center" }}
              >
                <Typography
                  sx={{
                    display: "block",
                    fontSize: { xs: "3rem", md: "5.5rem" },
                    fontWeight: 700,
                    letterSpacing: "-0.04em",
                    lineHeight: 1,
                    background: GOLD_GRADIENT,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 1,
                  }}
                >
                  ouro.is
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                  }}
                >
                  Registered August 2024
                </Typography>
              </MotionBox>
              <MotionBox
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: 0.14 }}
                sx={{
                  ...cardSx,
                  p: "20px 28px",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography sx={{ fontSize: 28, lineHeight: 1 }} aria-hidden>
                  🇮🇸
                </Typography>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: colors.text.muted,
                      mb: 0.4,
                    }}
                  >
                    Top-level domain origin
                  </Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: colors.text.primary }}>
                    Iceland — in honour of Björn
                  </Typography>
                </Box>
              </MotionBox>
              <MotionBox
                {...REVEAL}
                transition={{ ...REVEAL_TRANSITION, delay: 0.2 }}
                sx={{ ...cardSx, p: "20px 28px" }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    mb: 1,
                  }}
                >
                  The detail that matters
                </Typography>
                <Typography sx={{ fontSize: "13px", color: colors.text.secondary, lineHeight: 1.7 }}>
                  We are just like that — caring about the little details and appreciating the people we meet
                  along the journey.
                </Typography>
              </MotionBox>
            </Box>
          </Box>
        </PageContainer>
      </Box>

      <Box component="section" sx={sectionSx}>
        <PageContainer
          sx={{
            maxWidth: "75rem",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            alignItems: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          <MotionBox {...REVEAL} transition={REVEAL_TRANSITION}>
            <Eyebrow>Join the founding community</Eyebrow>
            <Typography component="h2" sx={{ ...titleSx, fontSize: { xs: "1.75rem", md: "3rem" } }}>
              Your record starts
              <br />
              the moment you do.
            </Typography>
            <Typography sx={{ ...descSx, maxWidth: "28rem" }}>
              Every founding player who joins raises the floor for everyone who comes after. Your region needs
              density — and it starts with you.
            </Typography>
          </MotionBox>
          <MotionBox
            {...REVEAL}
            transition={{ ...REVEAL_TRANSITION, delay: 0.08 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Box component={Link} to={{ pathname: "/" }} sx={btnPrimarySx}>
              Join Ouro
            </Box>
            <Box component={Link} to="/pricing" sx={btnGhostSx}>
              See pricing →
            </Box>
          </MotionBox>
        </PageContainer>
      </Box>
    </Box>
  );
}
